import axios from 'axios';

import { COREAPI, getUserData } from '../constants/global';
import type { IResultAnalyticsDataResponse } from '../interface/state/IElectionState';

// Standalone client for the Core API's ResultAnalytics endpoint, used to fetch a
// DESCENDANT subtree (grandchildren and deeper) when the agent drills into a child
// row. It attaches the agent's existing core JWT + PartyId (the same headers the
// Redux thunk sends), but has NO 401->logout interceptor: this is auxiliary view
// data, so a failure here must never sign the agent out or fire the global error
// toast. Crucially, the PartyId header both isolates the query to the agent's party
// AND arms the backend's `statistics.total` roster-count branch (missing PartyId ⇒
// total silently returns 0). Fetching here keeps the deep level in local state and
// never clobbers the shared Redux `_election.get_ElectionResultAnalytics` slice that
// drives the root list.
const resultAnalyticsApi = axios.create({ baseURL: COREAPI });

resultAnalyticsApi.interceptors.request.use((config) => {
	const { token, user } = getUserData();
	if (token) config.headers.Authorization = `Bearer ${token}`;
	if (user?.PartyId) config.headers.PartyId = user.PartyId;
	return config;
});

// The core service sometimes returns JSON with a Content-Type that prevents axios
// from auto-parsing — coerce string bodies back to JSON.
resultAnalyticsApi.interceptors.response.use((response) => {
	if (typeof response.data === 'string' && response.data) {
		try {
			response.data = JSON.parse(response.data);
		} catch {
			// Not JSON — leave as-is.
		}
	}
	return response;
});

export interface IAnalyticsScopeParams {
	electionId: number;
	// Child level to enumerate, lowercase: 'ec8a' | 'ec8b' | 'ec8c'.
	resultType: string;
	// Exactly one scope id is set — the numeric id of the entity being drilled into.
	stateId?: number;
	lgaId?: number;
	wardId?: number;
	pageNumber?: number;
	pageSize?: number;
	// Which source's rows to list. Defaults to 'Agent' (omitting it quadruples
	// rows). Pass 'IRev' to enumerate the IReV roll-up for the reconciliation
	// tiebreaker column.
	resultSource?: string;
}

// Discriminated on `reachable`: false ⇒ the server was never reached (network down,
// timeout, DNS, CORS). true ⇒ the server responded; inspect httpStatus and body.
export type AnalyticsScopeOutcome =
	| { reachable: false }
	| { reachable: true; httpStatus: number; body: IResultAnalyticsDataResponse | null };

// Large default so Kanban/Tree can group/expand a whole level in one page; a single
// level rarely exceeds this, and the caller's pager remains as a fallback.
const DEFAULT_PAGE_SIZE = 100;

/**
 * Fetch a descendant subtree's result analytics, scoped one rung below the drilled
 * entity. Sends `ResultSource=Agent` (omitting it quadruples rows across
 * Citizen/Agent/IRev/AI) plus the numeric scope id + lowercase `ResultType`, which
 * together arm the backend's roster-total branch. Non-2xx responses resolve
 * (validateStatus) so the caller can classify without an exception.
 */
export const getResultAnalyticsByScope = async (params: IAnalyticsScopeParams): Promise<AnalyticsScopeOutcome> => {
	const query = new URLSearchParams();
	query.append('ElectionId', String(params.electionId));
	query.append('ResultType', params.resultType);
	query.append('ResultSource', params.resultSource ?? 'Agent');
	query.append('PageNumber', String(params.pageNumber ?? 1));
	query.append('PageSize', String(params.pageSize ?? DEFAULT_PAGE_SIZE));
	if (params.stateId) query.append('StateId', String(params.stateId));
	if (params.lgaId) query.append('LgaId', String(params.lgaId));
	if (params.wardId) query.append('WardId', String(params.wardId));

	try {
		const res = await resultAnalyticsApi.get<IResultAnalyticsDataResponse>('/api/v1/ResultAnalytics', {
			params: query,
			validateStatus: () => true,
		});
		return { reachable: true, httpStatus: res.status, body: res.data };
	} catch {
		return { reachable: false };
	}
};
